import { useEffect, useState } from "react";
import { Mail, Loader2, CheckCircle2, XCircle, Send, Settings2, ScrollText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

const STATUS_BADGE = { sent: "bg-emerald-100 text-emerald-700", failed: "bg-red-100 text-red-700", delivered: "bg-blue-100 text-blue-700" };

export default function AdminEmail() {
  const [tab, setTab] = useState("log");
  const [log, setLog] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ from_name: "", reply_to: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");

  const loadLog = () => adminApi.get("/admin/email-log?limit=200").then((r) => setLog(r.data)).catch(() => {});
  const loadSettings = () => adminApi.get("/admin/email-settings").then((r) => { setSettings(r.data); setForm({ from_name: r.data.from_name, reply_to: r.data.reply_to }); }).catch(() => {});
  useEffect(() => { loadLog(); loadSettings(); }, []);

  const saveSettings = async () => {
    setBusy("save");
    try { await adminApi.put("/admin/email-settings", form); toast.success("Email settings saved."); loadSettings(); }
    catch { toast.error("Could not save settings."); }
    finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try { const r = await adminApi.post("/admin/email-settings/test", { to: testTo || undefined }); toast.success(`Test email sent via ${r.data.provider} to ${r.data.to}`); loadLog(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Test email failed."); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-email-view">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><Mail className="h-6 w-6 text-blue-600" /> Email</h1>
        <p className="text-slate-500 mt-1">Delivery log, sender settings and connection status for OperaMind emails.</p>
      </div>

      <div className="mt-4 flex gap-2 border-b border-slate-200">
        <button data-testid="tab-log" onClick={() => setTab("log")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "log" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"}`}><ScrollText className="h-4 w-4" /> Email Log</button>
        <button data-testid="tab-settings" onClick={() => setTab("settings")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1.5 ${tab === "settings" ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500"}`}><Settings2 className="h-4 w-4" /> Settings</button>
      </div>

      {tab === "log" && (
        <div className="mt-4">
          {log && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="email-stat-sent"><p className="font-heading text-2xl font-extrabold text-emerald-600">{log.stats.sent}</p><p className="text-xs text-slate-500">Sent</p></div>
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="email-stat-failed"><p className="font-heading text-2xl font-extrabold text-red-500">{log.stats.failed}</p><p className="text-xs text-slate-500">Failed</p></div>
              <div className="rounded-xl border border-slate-200 bg-white p-4"><p className="font-heading text-2xl font-extrabold text-slate-900">{log.stats.total}</p><p className="text-xs text-slate-500">Total · via {log.provider}</p></div>
            </div>
          )}
          <div className="flex justify-end mb-2"><Button data-testid="refresh-log" onClick={loadLog} variant="outline" size="sm" className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button></div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr><th className="text-left font-semibold px-4 py-3">Recipient</th><th className="text-left font-semibold px-4 py-3">Subject</th><th className="text-left font-semibold px-4 py-3">Status</th><th className="text-left font-semibold px-4 py-3">Date & time</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!log && <tr><td colSpan={4} className="py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
                {log && log.logs.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-slate-400">No emails sent yet.</td></tr>}
                {log && log.logs.map((l, i) => (
                  <tr key={i} data-testid="email-log-row" className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{l.to || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{l.subject}<span className="text-slate-300"> · {l.type}</span></td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full text-xs font-semibold px-2 py-0.5 ${STATUS_BADGE[l.status] || "bg-slate-100 text-slate-500"}`}>{l.status || (l.sent ? "sent" : "failed")}</span>
                      {l.error && <p className="text-xs text-red-400 mt-0.5 max-w-xs truncate" title={l.error}>{l.error}</p>}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "settings" && settings && (
        <div className="mt-4 max-w-lg space-y-4">
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${settings.status?.connected ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid="email-connection-status">
            {settings.status?.connected ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-red-500" />}
            <div>
              <p className={`font-semibold text-sm ${settings.status?.connected ? "text-emerald-700" : "text-red-700"}`}>{settings.status?.connected ? "Connected" : "Not connected"} · {settings.provider}</p>
              <p className="text-xs text-slate-500">Sending from {settings.from_email}{settings.status?.error ? ` — ${settings.status.error}` : ""}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
            <div><Label className="text-xs">Sender name</Label><Input data-testid="setting-from-name" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Reply-to address</Label><Input data-testid="setting-reply-to" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">From address (managed via environment)</Label><Input value={settings.from_email} disabled className="mt-1 bg-slate-50" /></div>
            <Button data-testid="save-email-settings" onClick={saveSettings} disabled={busy === "save"} className="bg-blue-600 hover:bg-blue-700 gap-2">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save settings</Button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="font-semibold text-sm mb-2">Send a test email</p>
            <div className="flex gap-2">
              <Input data-testid="test-email-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder={settings.from_email} />
              <Button data-testid="send-test-email" onClick={sendTest} disabled={busy === "test"} className="gap-2 bg-slate-900 hover:bg-slate-800">{busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
